import type { ReactNode } from "react";

interface SlideShellProps {
  eyebrow?: string;
  page: number;
  total: number;
  children: ReactNode;
  scroll?: boolean;
}

const SlideShell = ({ eyebrow, page, total, children, scroll = true }: SlideShellProps) => {
  return (
    <div
      className={`h-full w-full ${scroll ? "overflow-y-auto" : "overflow-hidden"} px-6 sm:px-10 lg:px-16 pt-14 sm:pt-16 pb-28`}
    >
      <div className="max-w-7xl mx-auto h-full flex flex-col">
        {/* Mini chrome — eyebrow + page indicator */}
        <div className="flex items-center justify-between mb-5 sm:mb-6">
          <div className="flex items-center gap-3">
            <img
              src="/branding/helpgenie/helpgenie-wordmark.png"
              alt="Help Genie"
              className="h-6 sm:h-7 w-auto"
            />
            {eyebrow && (
              <>
                <span className="text-gray-300 hidden sm:inline">·</span>
                <span className="hidden sm:inline text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-[#FF4DB8]">
                  {eyebrow}
                </span>
              </>
            )}
          </div>
          <div className="text-[10px] sm:text-xs font-semibold uppercase tracking-[0.18em] text-[#64748B]">
            Page {String(page).padStart(2, "0")}{" "}
            <span className="text-gray-300">/ {String(total).padStart(2, "0")}</span>
          </div>
        </div>

        {/* Gradient rule */}
        <div
          className="h-[3px] rounded-full mb-6 sm:mb-8 opacity-90"
          style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #D6409F 50%, #4E9CFF 100%)" }}
        />

        {/* Body */}
        <div className="flex-1 min-h-0">{children}</div>
      </div>
    </div>
  );
};

export default SlideShell;

// Small atoms shared across slides
export const Eyebrow = ({ children }: { children: ReactNode }) => (
  <div className="sm:hidden text-[10px] font-semibold uppercase tracking-[0.18em] text-[#FF4DB8] mb-2">
    {children}
  </div>
);

export const Title = ({ children }: { children: ReactNode }) => (
  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#222B45] leading-[1.06] tracking-tight">
    {children}
  </h2>
);

export const Grad = ({ children }: { children: ReactNode }) => (
  <span
    className="bg-clip-text text-transparent"
    style={{ backgroundImage: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
  >
    {children}
  </span>
);

export const Lead = ({ children }: { children: ReactNode }) => (
  <p className="text-base sm:text-lg text-[#64748B] leading-relaxed max-w-3xl">{children}</p>
);

export const Body = ({ children }: { children: ReactNode }) => (
  <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{children}</p>
);

export const Callout = ({ children }: { children: ReactNode }) => (
  <div
    className="rounded-xl bg-[#F4F6FB] border-l-4 px-5 py-4 text-sm sm:text-base text-[#222B45] leading-relaxed"
    style={{ borderImage: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%) 1", borderLeftWidth: 4 }}
  >
    {children}
  </div>
);

export const Bullets = ({ items }: { items: string[] }) => (
  <ul className="space-y-2.5">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3 text-sm sm:text-base text-gray-700 leading-relaxed">
        <span
          className="mt-2 flex-shrink-0 h-2 w-2 rounded-sm"
          style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
        />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);
